import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Phone, WhatsappLogo, EnvelopeSimple, ArrowLeft, NotePencil, CalendarBlank, ChatCircle, Clock, X, Trash, ArrowsLeftRight, Megaphone, Link as LinkIcon, MapPin, DeviceMobile, Copy } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const STAGES = ['new_inquiry', 'contacted', 'qualified', 'demo_scheduled', 'demo_attended', 'application', 'fee_pending', 'enrolled', 'lost', 'on_hold'];
const STAGE_ACCENT = {
  new_inquiry: '#2B6EF0', contacted: '#06B6D4', qualified: '#8B5CF6',
  demo_scheduled: '#E5A100', demo_attended: '#F97316', application: '#6366F1',
  fee_pending: '#EAB308', enrolled: '#10A882', lost: '#EF4444', on_hold: '#808080',
};

const ACT_ICONS = { note: NotePencil, call: Phone, whatsapp: WhatsappLogo, email: EnvelopeSimple, stage_change: Clock, task: CalendarBlank, event: CalendarBlank };
const ACT_COLOR = { note: '#808080', call: '#10A882', whatsapp: '#25D366', email: '#2B6EF0', stage_change: '#8B5CF6', task: '#E5A100', event: '#E5A100' };

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('note');
  const [showScheduleDemo, setShowScheduleDemo] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);

  const fetchData = async () => {
    try {
      const [lRes, aRes] = await Promise.all([api.get(`/api/leads/${id}`), api.get(`/api/leads/${id}/activities`)]);
      setLead(lRes.data);
      setActivities(aRes.data.activities || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchCalendarEvents = async () => {
    try {
      const res = await api.get(`/api/calendar/events?lead_id=${id}`);
      setCalendarEvents(res.data.events || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchData(); fetchCalendarEvents(); }, [id]);

  const handleStageChange = async (stage) => {
    try { await api.put(`/api/leads/${id}/stage`, { stage }); toast.success('Stage updated'); fetchData(); } catch (e) { toast.error('Failed'); }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!noteContent.trim()) return;
    try { await api.post(`/api/leads/${id}/activities`, { type: noteType, content: noteContent }); setNoteContent(''); toast.success('Added'); fetchData(); } catch (e) { toast.error('Failed'); }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/api/calendar/events/${eventId}`);
      toast.success('Event cancelled');
      fetchCalendarEvents();
      fetchData();
    } catch (e) { toast.error('Failed to cancel event'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground font-mono">loading...</div>;
  if (!lead) return <div className="text-sm text-muted-foreground">Lead not found</div>;

  const accent = STAGE_ACCENT[lead.stage] || '#808080';

  return (
    <div data-testid="lead-detail-page" className="stagger-in">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} data-testid="back-to-leads" className="h-8 px-2 rounded-lg text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-extrabold text-foreground tracking-tight">{lead.student_name}</h1>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded" style={{ color: accent, backgroundColor: `${accent}15` }}>
              <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: accent }} />
              {lead.stage?.replace('_', ' ')}
            </span>
            <span className="text-[11px] font-mono text-muted-foreground">Score: {lead.lead_score}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">{lead.source} &middot; {lead.city}, {lead.area}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" data-testid="transfer-lead-btn" onClick={() => setShowTransfer(true)} className="h-8 rounded-lg text-xs gap-1" style={{ color: '#6366F1', borderColor: '#6366F140' }}><ArrowsLeftRight size={12} /> Transfer</Button>
          <Button variant="outline" size="sm" data-testid="schedule-demo-btn" onClick={() => setShowScheduleDemo(true)} className="h-8 rounded-lg text-xs gap-1" style={{ color: '#E5A100', borderColor: '#E5A10040' }}><CalendarBlank size={12} /> Schedule Demo</Button>
          <Button variant="outline" size="sm" data-testid="lead-call-btn" className="h-8 rounded-lg text-xs gap-1"><Phone size={12} /> Call</Button>
          <Button variant="outline" size="sm" data-testid="lead-whatsapp-btn" className="h-8 rounded-lg text-xs gap-1" style={{ color: '#25D366', borderColor: '#25D36640' }}><WhatsappLogo size={12} /> WhatsApp</Button>
          <Button variant="outline" size="sm" data-testid="lead-email-btn" className="h-8 rounded-lg text-xs gap-1"><EnvelopeSimple size={12} /> Email</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main */}
        <div className="lg:col-span-2 space-y-4">
          {/* Stage selector */}
          <Card className="border border-border rounded-xl">
            <CardContent className="p-4">
              <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] mb-2.5 font-bold">Pipeline Stage</p>
              <div className="flex flex-wrap gap-1.5">
                {STAGES.map(s => {
                  const sa = STAGE_ACCENT[s] || '#808080';
                  const active = lead.stage === s;
                  return (
                    <button key={s} onClick={() => handleStageChange(s)} data-testid={`stage-btn-${s}`}
                      className="text-[10px] px-3 py-1.5 rounded-lg border transition-all font-semibold uppercase tracking-wider"
                      style={{
                        backgroundColor: active ? sa : 'transparent',
                        color: active ? '#fff' : 'hsl(var(--muted-foreground))',
                        borderColor: active ? sa : 'hsl(var(--border))',
                      }}
                    >
                      {s.replace('_', ' ')}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Events for this Lead */}
          {calendarEvents.length > 0 && (
            <Card className="border border-border rounded-xl" data-testid="lead-calendar-events">
              <CardContent className="p-4">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] mb-2.5 font-bold">Scheduled Events</p>
                <div className="space-y-0">
                  {calendarEvents.map((evt) => (
                    <div key={evt._id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#E5A10015', color: '#E5A100' }}>
                        <CalendarBlank size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] text-foreground font-medium">{evt.summary}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                          {evt.start_time ? new Date(evt.start_time).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                          {evt.location ? ` \u00b7 ${evt.location}` : ''}
                          {evt.google_link ? <> &middot; <a href={evt.google_link} target="_blank" rel="noopener noreferrer" className="underline">Google Calendar</a></> : ''}
                        </p>
                      </div>
                      <button onClick={() => handleDeleteEvent(evt._id)} data-testid={`cancel-event-${evt._id}`} className="text-muted-foreground hover:text-red-500 transition-colors p-1">
                        <Trash size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add Activity */}
          <Card className="border border-border rounded-xl">
            <CardContent className="p-4">
              <form onSubmit={handleAddActivity} className="flex gap-2">
                <select value={noteType} onChange={e => setNoteType(e.target.value)} data-testid="activity-type" className="h-9 rounded-lg text-xs border border-border px-2 bg-card text-foreground font-mono">
                  <option value="note">Note</option><option value="call">Call</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="task">Task</option>
                </select>
                <Input value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Add activity..." data-testid="activity-input" className="h-9 rounded-lg text-xs flex-1" />
                <Button type="submit" size="sm" data-testid="add-activity-btn" className="h-9 rounded-lg text-xs font-bold px-4 text-white" style={{ backgroundColor: '#E04B2A' }}>Add</Button>
              </form>
            </CardContent>
          </Card>

          {/* Timeline */}
          <div data-testid="lead-timeline">
            {activities.map((a, i) => {
              const Icon = ACT_ICONS[a.type] || ChatCircle;
              const color = ACT_COLOR[a.type] || '#808080';
              return (
                <div key={i} className="flex gap-3 py-3 border-b border-border last:border-0">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15`, color }}>
                    <Icon size={13} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-foreground">{a.content}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                      {a.user_name} &middot; {a.type?.replace('_', ' ')} &middot; {a.created_at ? new Date(a.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                </div>
              );
            })}
            {activities.length === 0 && <p className="text-xs text-muted-foreground text-center py-8 font-mono">no activities yet</p>}
          </div>
        </div>

        {/* Right - Profile */}
        <div className="space-y-4">
          <InfoCard title="Student Info" rows={[
            ['Grade', lead.grade], ['Board', lead.board], ['Target Exam', lead.target_exam],
            ['Timing', lead.preferred_timing], ['Email', lead.email], ['Phone', lead.student_phone],
          ]} />
          <InfoCard title="Parent / Guardian" rows={[
            ['Name', lead.parent_name], ['Phone', lead.parent_phone], ['WhatsApp', lead.whatsapp],
          ]} />
          <AttributionCard lead={lead} />
          <InfoCard title="Tracking" rows={[
            ['Source', lead.source], ['Campaign', lead.campaign], ['Medium', lead.medium], ['Consent', lead.consent_status],
          ]} />
          <InfoCard title="Dates" rows={[
            ['Inquiry', lead.inquiry_date ? new Date(lead.inquiry_date).toLocaleDateString('en-IN') : null],
            ['Last Contact', lead.last_contacted ? new Date(lead.last_contacted).toLocaleDateString('en-IN') : null],
            ['Follow-up', lead.next_follow_up ? new Date(lead.next_follow_up).toLocaleDateString('en-IN') : null],
          ]} />
        </div>
      </div>

      {/* Schedule Demo Dialog */}
      {showScheduleDemo && (
        <ScheduleDemoDialog
          lead={lead}
          onClose={() => setShowScheduleDemo(false)}
          onCreated={() => { setShowScheduleDemo(false); fetchCalendarEvents(); fetchData(); }}
        />
      )}

      {/* Transfer Lead Dialog */}
      {showTransfer && (
        <TransferLeadDialog
          leadIds={[lead.id]}
          onClose={() => setShowTransfer(false)}
          onTransferred={() => { setShowTransfer(false); fetchData(); }}
        />
      )}
    </div>
  );
}

function ScheduleDemoDialog({ lead, onClose, onCreated }) {
  const [summary, setSummary] = useState(`Demo \u2014 ${lead.student_name}`);
  const [startTime, setStartTime] = useState('');
  const [location, setLocation] = useState('');
  const [attendeeEmail, setAttendeeEmail] = useState(lead.email || '');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!summary.trim() || !startTime) { toast.error('Summary and date/time are required'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/calendar/events', {
        type: 'demo',
        lead_id: lead._id,
        summary,
        description,
        start_time: new Date(startTime).toISOString(),
        location,
        attendee_email: attendeeEmail,
      });
      toast.success('Demo scheduled');
      onCreated();
    } catch (err) {
      toast.error('Failed to schedule demo');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="schedule-demo-dialog">
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold text-foreground">Schedule Demo</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Summary</label>
            <Input value={summary} onChange={e => setSummary(e.target.value)} data-testid="demo-summary" className="h-9 rounded-lg text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Date & Time</label>
            <input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} data-testid="demo-start-time"
              className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono" required />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Location (optional)</label>
            <Input value={location} onChange={e => setLocation(e.target.value)} data-testid="demo-location" placeholder="Branch / online link" className="h-9 rounded-lg text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Attendee Email</label>
            <Input value={attendeeEmail} onChange={e => setAttendeeEmail(e.target.value)} data-testid="demo-attendee-email" className="h-9 rounded-lg text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Description (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} data-testid="demo-description" rows={2}
              className="w-full rounded-lg text-xs border border-border px-3 py-2 bg-card text-foreground resize-none" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="h-8 rounded-lg text-xs">Cancel</Button>
            <Button type="submit" size="sm" disabled={submitting} data-testid="demo-submit-btn"
              className="h-8 rounded-lg text-xs font-bold px-4 text-white" style={{ backgroundColor: '#2563EB' }}>
              {submitting ? 'Scheduling...' : 'Schedule'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TransferLeadDialog({ leadIds, onClose, onTransferred }) {
  const [branches, setBranches] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [counselorId, setCounselorId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/api/branches').then(r => setBranches(r.data.branches || r.data || [])).catch(console.error);
  }, []);

  useEffect(() => {
    if (!branchId) { setCounselors([]); setCounselorId(''); return; }
    api.get(`/api/users?branch_id=${branchId}&role=counselor`).then(r => setCounselors(r.data.users || r.data || [])).catch(console.error);
    setCounselorId('');
  }, [branchId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branchId) { toast.error('Select a branch'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.put('/api/leads/transfer', {
        lead_ids: leadIds,
        branch_id: branchId,
        counselor_id: counselorId,
        reason,
      });
      toast.success(`Transferred ${data.transferred} lead(s)`);
      onTransferred();
    } catch (err) {
      toast.error('Transfer failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="transfer-lead-dialog">
      <div className="bg-card border border-border rounded-xl w-full max-w-md p-5 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-bold text-foreground">Transfer Lead{leadIds.length > 1 ? 's' : ''}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Branch *</label>
            <select value={branchId} onChange={e => setBranchId(e.target.value)} data-testid="transfer-branch-select"
              className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono" required>
              <option value="">Select branch...</option>
              {branches.map(b => <option key={b.id || b._id} value={b.id || b._id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Counselor</label>
            <select value={counselorId} onChange={e => setCounselorId(e.target.value)} data-testid="transfer-counselor-select"
              className="w-full h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono">
              <option value="">Auto-assign (round robin)</option>
              {counselors.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold block mb-1">Reason</label>
            <Input value={reason} onChange={e => setReason(e.target.value)} data-testid="transfer-reason" placeholder="Why is this lead being transferred?" className="h-9 rounded-lg text-xs" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="h-8 rounded-lg text-xs">Cancel</Button>
            <Button type="submit" size="sm" disabled={submitting} data-testid="transfer-submit-btn"
              className="h-8 rounded-lg text-xs font-bold px-4 text-white" style={{ backgroundColor: '#2563EB' }}>
              {submitting ? 'Transferring...' : `Transfer ${leadIds.length > 1 ? leadIds.length + ' Leads' : 'Lead'}`}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AttributionCard({ lead }) {
  const utms = [
    ['utm_source', lead.utm_source],
    ['utm_medium', lead.utm_medium],
    ['utm_campaign', lead.utm_campaign],
    ['utm_term', lead.utm_term],
    ['utm_content', lead.utm_content],
  ].filter(([, v]) => v);

  const hasAttribution = lead.campaign_id || lead.campaign_name || lead.ad_group || lead.ad_id ||
    lead.gclid || lead.fbclid || utms.length > 0 || lead.landing_page || lead.referrer_url ||
    lead.city || lead.device_type || lead.duplicate_of;

  if (!hasAttribution && !lead.source) return null;

  const touchpoints = Array.isArray(lead.touchpoints) ? lead.touchpoints : [];

  return (
    <Card className="border border-border rounded-xl" data-testid="attribution-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone size={13} className="text-muted-foreground" />
          <h3 className="text-[9px] font-bold text-muted-foreground uppercase tracking-[0.12em]">Attribution</h3>
        </div>
        <div className="space-y-2 text-[12px]">
          {lead.source && (
            <Row label="Source"><span className="font-mono text-foreground">{lead.source}</span></Row>
          )}
          {lead.campaign_name && (
            <Row label="Campaign"><span className="text-foreground font-medium">{lead.campaign_name}</span></Row>
          )}
          {lead.campaign_id && !lead.campaign_name && (
            <Row label="Campaign ID"><span className="font-mono text-[10px] text-foreground/80 break-all">{lead.campaign_id}</span></Row>
          )}
          {lead.ad_group && <Row label="Ad Group"><span className="font-mono text-[10px] text-foreground/80">{lead.ad_group}</span></Row>}
          {lead.ad_id && <Row label="Ad ID"><span className="font-mono text-[10px] text-foreground/80 break-all">{lead.ad_id}</span></Row>}
          {lead.ad_creative && <Row label="Creative"><span className="font-mono text-[10px] text-foreground/80 break-all">{lead.ad_creative}</span></Row>}

          {utms.length > 0 && (
            <div className="pt-2 mt-2 border-t border-border">
              <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">UTM Parameters</p>
              <div className="space-y-1">
                {utms.map(([k, v]) => (
                  <div key={k} className="flex items-start gap-2 text-[10px]">
                    <span className="font-mono text-muted-foreground w-24 shrink-0">{k}</span>
                    <span className="font-mono text-foreground/90 break-all">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(lead.gclid || lead.fbclid) && (
            <div className="pt-2 mt-2 border-t border-border space-y-1">
              {lead.gclid && (
                <div className="flex items-start gap-2 text-[10px]">
                  <span className="font-mono text-muted-foreground w-16 shrink-0">gclid</span>
                  <span className="font-mono text-foreground/90 break-all">{lead.gclid}</span>
                </div>
              )}
              {lead.fbclid && (
                <div className="flex items-start gap-2 text-[10px]">
                  <span className="font-mono text-muted-foreground w-16 shrink-0">fbclid</span>
                  <span className="font-mono text-foreground/90 break-all">{lead.fbclid}</span>
                </div>
              )}
            </div>
          )}

          {(lead.landing_page || lead.referrer_url) && (
            <div className="pt-2 mt-2 border-t border-border space-y-1.5">
              {lead.landing_page && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-0.5">Landing Page</p>
                  <a href={lead.landing_page} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-foreground/90 break-all underline hover:text-foreground inline-flex items-center gap-1">
                    <LinkIcon size={10} />{lead.landing_page}
                  </a>
                </div>
              )}
              {lead.referrer_url && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-0.5">Referrer</p>
                  <a href={lead.referrer_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-foreground/90 break-all underline hover:text-foreground inline-flex items-center gap-1">
                    <LinkIcon size={10} />{lead.referrer_url}
                  </a>
                </div>
              )}
            </div>
          )}

          {(lead.city || lead.device_type) && (
            <div className="pt-2 mt-2 border-t border-border flex items-center gap-3 text-[10px] text-muted-foreground font-mono">
              {lead.city && <span className="inline-flex items-center gap-1"><MapPin size={11} />{lead.city}</span>}
              {lead.device_type && <span className="inline-flex items-center gap-1"><DeviceMobile size={11} />{lead.device_type}</span>}
            </div>
          )}

          {lead.duplicate_of && (
            <div className="pt-2 mt-2 border-t border-border">
              <Link to={`/leads/${lead.duplicate_of}`} className="text-[11px] inline-flex items-center gap-1 text-[#2563EB] hover:underline" data-testid="duplicate-of-link">
                <Copy size={11} /> Duplicate of original lead
              </Link>
            </div>
          )}
        </div>

        {touchpoints.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Touchpoint History</p>
            <div className="space-y-1.5" data-testid="touchpoint-list">
              {touchpoints.map((tp, i) => (
                <div key={i} className="flex items-start gap-2 text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-sm mt-1.5 shrink-0" style={{ backgroundColor: '#E04B2A' }} />
                  <div className="flex-1 min-w-0">
                    <span className="text-foreground/90 font-medium">{tp.source || tp.channel || tp.type || 'touch'}</span>
                    {tp.campaign && <span className="text-muted-foreground"> &middot; {tp.campaign}</span>}
                    {tp.timestamp && (
                      <div className="text-muted-foreground font-mono mt-0.5">
                        {new Date(tp.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-muted-foreground w-20 shrink-0 text-[11px]">{label}</span>
      <span className="text-foreground flex-1 min-w-0">{children}</span>
    </div>
  );
}

function InfoCard({ title, rows }) {
  return (
    <Card className="border border-border rounded-xl">
      <CardContent className="p-4">
        <h3 className="text-[9px] font-bold text-muted-foreground mb-3 uppercase tracking-[0.12em]">{title}</h3>
        <div className="space-y-2 text-[12px]">
          {rows.map(([label, value]) => (
            <div key={label} className="flex items-start gap-2">
              <span className="text-muted-foreground w-20 shrink-0 text-[11px]">{label}</span>
              <span className="text-foreground">{value || '-'}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
